import { OpenFeature } from '@openfeature/web-sdk';

import type { AccountState } from '../../reducers/account.reducer';
import {
  type CurrentAppContext,
  buildFeatureFlagEvaluationContext,
  buildHarnessOrgFeatureFlagEvaluationContext,
} from './core/featureFlagContext';
import {
  HARNESS_ORG_OPENFEATURE_DOMAIN,
  initializeHarnessFeatureFlags as initializeHarnessProvider,
} from './providers/harnessProvider';

let activeContextKey: string | undefined;
let activeContextUpdate: Promise<boolean> | undefined;

let activeOrgContextKey: string | undefined;
let activeOrgContextUpdate: Promise<boolean> | undefined;

let activeCombinedContextKey: string | undefined;
let activeCombinedContextUpdate: Promise<boolean> | undefined;

export function initializeFeatureFlags(): void {
  initializeHarnessProvider();
}

export function setFeatureFlagContext(
  account: AccountState,
  currentApp: CurrentAppContext,
  isFlipdishStaff: boolean
): Promise<boolean> {
  const userEvaluationContext = buildFeatureFlagEvaluationContext(
    account,
    currentApp,
    isFlipdishStaff
  );
  const orgEvaluationContext = buildHarnessOrgFeatureFlagEvaluationContext(currentApp);
  const combinedContextKey = JSON.stringify({
    user: userEvaluationContext,
    org: orgEvaluationContext,
  });

  if (combinedContextKey === activeCombinedContextKey && activeCombinedContextUpdate) {
    return activeCombinedContextUpdate;
  }

  const contextUpdate = Promise.all([
    setGlobalFeatureFlagContext(account, currentApp, isFlipdishStaff),
    setHarnessOrgFeatureFlagContext(currentApp),
  ]).then(([globalContextUpdated, orgContextUpdated]) => {
    const succeeded = globalContextUpdated && orgContextUpdated;
    if (!succeeded && activeCombinedContextUpdate === contextUpdate) {
      activeCombinedContextKey = undefined;
      activeCombinedContextUpdate = undefined;
    }
    return succeeded;
  });

  activeCombinedContextKey = combinedContextKey;
  activeCombinedContextUpdate = contextUpdate;
  return contextUpdate;
}

function setGlobalFeatureFlagContext(
  account: AccountState,
  currentApp: CurrentAppContext,
  isFlipdishStaff: boolean
): Promise<boolean> {
  const evaluationContext = buildFeatureFlagEvaluationContext(account, currentApp, isFlipdishStaff);
  const contextKey = JSON.stringify(evaluationContext);

  if (contextKey === activeContextKey && activeContextUpdate) {
    return activeContextUpdate;
  }

  const contextUpdate = OpenFeature.setContext(evaluationContext).then(
    () => true,
    (error: unknown) => {
      if (activeContextUpdate === contextUpdate) {
        activeContextKey = undefined;
        activeContextUpdate = undefined;
      }
      console.error('[FeatureFlags] Failed to update OpenFeature context.', error);
      return false;
    }
  );

  activeContextKey = contextKey;
  activeContextUpdate = contextUpdate;
  return contextUpdate;
}

function setHarnessOrgFeatureFlagContext(currentApp: CurrentAppContext): Promise<boolean> {
  const evaluationContext = buildHarnessOrgFeatureFlagEvaluationContext(currentApp);
  const contextKey = JSON.stringify(evaluationContext);

  if (contextKey === activeOrgContextKey && activeOrgContextUpdate) {
    return activeOrgContextUpdate;
  }

  const contextUpdate = OpenFeature.setContext(
    HARNESS_ORG_OPENFEATURE_DOMAIN,
    evaluationContext
  ).then(
    () => true,
    (error: unknown) => {
      if (activeOrgContextUpdate === contextUpdate) {
        activeOrgContextKey = undefined;
        activeOrgContextUpdate = undefined;
      }
      console.error(
        '[FeatureFlags] Failed to update Harness org-keyed OpenFeature context.',
        error
      );
      return false;
    }
  );

  activeOrgContextKey = contextKey;
  activeOrgContextUpdate = contextUpdate;
  return contextUpdate;
}
